'use client';

import Image, { type ImageProps } from 'next/image';
import { useEffect, useState } from 'react';

const EMPTY_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
const pending = new Map<Element, () => void>();
let observer: IntersectionObserver | null = null;

function observe(element: Element, load: () => void): () => void {
  if (!observer) {
    observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRect.width <= 0 || entry.intersectionRect.height <= 0) continue;
        const callback = pending.get(entry.target);
        pending.delete(entry.target);
        observer?.unobserve(entry.target);
        callback?.();
      }
    }, { rootMargin: '0px', threshold: 0 });
  }
  pending.set(element, load);
  observer.observe(element);
  return () => {
    pending.delete(element);
    observer?.unobserve(element);
    if (pending.size === 0) { observer?.disconnect(); observer = null; }
  };
}

/** Keep full image quality; only assign the network URL once a non-critical image is visible. */
export default function ViewportImage(props: ImageProps) {
  const eager = Boolean(props.priority || props.preload || props.loading === 'eager' || props.fetchPriority === 'high');
  const [visible, setVisible] = useState(eager);
  const [observedNode, setObservedNode] = useState<HTMLImageElement | null>(null);
  const ready = eager || visible;

  useEffect(() => {
    if (ready) return;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    if (!observedNode) return;
    return observe(observedNode, () => setVisible(true));
  }, [ready, observedNode]);

  const setImgRef = (node: HTMLImageElement | null) => {
    setObservedNode(node);
  };

  if (eager) return <Image {...props} />;

  return <>
    <Image
      {...props}
      ref={setImgRef}
      src={ready ? props.src : EMPTY_PIXEL}
      loading={ready ? (props.loading ?? 'lazy') : 'eager'}
      onLoad={ready ? props.onLoad : undefined}
      onLoadingComplete={ready ? props.onLoadingComplete : undefined}
      onError={ready ? props.onError : undefined}
      data-image-deferred={ready ? undefined : 'true'}
    />
    <noscript><Image {...props} loading="lazy" /></noscript>
  </>;
}
